import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Plus, 
  Search, 
  MapPin, 
  Edit, 
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2
} from "lucide-react";
import { Office, OfficeFormData } from "@/types/office";
import { 
  getAllOffices, 
  createOffice, 
  updateOffice, 
  deleteOffice,
  activateOffice 
} from "@/services/officeService";
import { OfficeFormSheet } from "./OfficeFormSheet";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function OfficeManagement() {
  const { user } = useAuth();
  const [offices, setOffices] = useState<Office[]>([]);
  const [filteredOffices, setFilteredOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [officeToDelete, setOfficeToDelete] = useState<Office | null>(null);

  // Load offices
  useEffect(() => {
    loadOffices();
  }, []);

  // Filter offices based on search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredOffices(offices);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredOffices(
        offices.filter(
          (office) =>
            office.name.toLowerCase().includes(query) ||
            office.city.toLowerCase().includes(query) ||
            office.address.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, offices]);

  const loadOffices = async () => {
    setLoading(true);
    try {
      const data = await getAllOffices();
      setOffices(data);
    } catch (error) {
      console.error("Error loading offices:", error);
      toast.error("Failed to load offices");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOffice = () => {
    setSelectedOffice(null);
    setFormDialogOpen(true);
  };

  const handleEditOffice = (office: Office) => {
    setSelectedOffice(office);
    setFormDialogOpen(true);
  };

  const handleSaveOffice = async (formData: OfficeFormData) => {
    try {
      if (selectedOffice) {
        // Update existing office
        await updateOffice(selectedOffice.id, formData);
        toast.success("Office updated successfully!");
      } else {
        // Create new office
        if (!user?.id) {
          toast.error("User not authenticated");
          return;
        }
        await createOffice(formData, user.id);
        toast.success("Office created successfully!");
      }
      await loadOffices();
    } catch (error) {
      console.error("Error saving office:", error);
      toast.error("Failed to save office");
      throw error;
    }
  };

  const handleDeleteClick = (office: Office) => {
    setOfficeToDelete(office);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!officeToDelete) return;

    try {
      await deleteOffice(officeToDelete.id);
      toast.success("Office deactivated successfully!");
      await loadOffices();
    } catch (error) {
      console.error("Error deleting office:", error);
      toast.error("Failed to deactivate office");
    } finally {
      setDeleteDialogOpen(false);
      setOfficeToDelete(null);
    }
  };

  const handleActivateOffice = async (office: Office) => {
    try {
      await activateOffice(office.id);
      toast.success("Office activated successfully!");
      await loadOffices();
    } catch (error) {
      console.error("Error activating office:", error);
      toast.error("Failed to activate office");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2 min-w-0">
          <Building2 className="h-5 w-5 shrink-0 text-green-600" />
          <span className="truncate">Office Management</span>
        </h2>
        <Button
          onClick={handleAddOffice}
          size="sm"
          className="shrink-0 gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md"
        >
          <Plus className="h-4 w-4" />
          Add Office
        </Button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by office name, city, or address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Office Cards - Mobile View */}
      <div className="md:hidden space-y-3">
        {filteredOffices.length === 0 ? (
          <Card variant="elevated">
            <CardContent className="py-8 text-center text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                {searchQuery ? "No offices found" : "No offices added yet"}
              </p>
              {!searchQuery && (
                <Button onClick={handleAddOffice} variant="outline" size="sm" className="mt-3">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Office
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredOffices.map((office, index) => (
            <motion.div
              key={office.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                variant="elevated" 
                className={cn(
                  "transition-all",
                  !office.isActive && "opacity-60 bg-secondary/30"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base truncate">{office.name}</h3>
                      <p className="text-sm text-muted-foreground">{office.city}</p>
                    </div>
                    <Badge variant={office.isActive ? "success" : "secondary"} className="flex-shrink-0">
                      {office.isActive ? (
                        <>
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-3">
                    <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{office.address}</span>
                    </p>
                    {office.geoLocation && office.geoLocation.latitude !== 0 && office.geoLocation.longitude !== 0 ? (
                    <a
                      href={`https://www.google.com/maps?q=${office.geoLocation.latitude},${office.geoLocation.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                    >
                      <MapPin className="h-3 w-3" />
                      View on Map
                    </a>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditOffice(office)}
                      className="flex-1"
                    >
                      <Edit className="h-3.5 w-3.5 mr-1.5" />
                      Edit
                    </Button>
                    {office.isActive ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(office)}
                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleActivateOffice(office)}
                        className="flex-1 border-green-200 text-green-600 hover:bg-green-50"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Activate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Office Table - Desktop View */}
      <div className="hidden md:block">
        <Card variant="elevated">
          <CardContent className="p-0">
            {filteredOffices.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Building2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-base mb-2">
                  {searchQuery ? "No offices found" : "No offices added yet"}
                </p>
                {!searchQuery && (
                  <Button onClick={handleAddOffice} variant="outline" size="sm" className="mt-3">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Office
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-semibold text-sm">Office Name</th>
                      <th className="text-left p-4 font-semibold text-sm">City</th>
                      <th className="text-left p-4 font-semibold text-sm">Address</th>
                      <th className="text-left p-4 font-semibold text-sm">Location</th>
                      <th className="text-center p-4 font-semibold text-sm">Status</th>
                      <th className="text-center p-4 font-semibold text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOffices.map((office, index) => (
                      <motion.tr
                        key={office.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className={cn(
                          "border-b hover:bg-muted/30 transition-colors",
                          !office.isActive && "opacity-60 bg-secondary/20"
                        )}
                      >
                        <td className="p-4">
                          <div className="font-medium">{office.name}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm text-muted-foreground">{office.city}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm text-muted-foreground max-w-xs truncate">
                            {office.address}
                          </div>
                        </td>
                        <td className="p-4">
                          {office.geoLocation &&
                          !(office.geoLocation.latitude === 0 && office.geoLocation.longitude === 0) ? (
                          <a
                            href={`https://www.google.com/maps?q=${office.geoLocation.latitude},${office.geoLocation.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                          >
                            <MapPin className="h-3 w-3" />
                            View Map
                          </a>
                          ) : (<span className="text-xs text-muted-foreground">—</span>)}
                        </td>
                        <td className="p-4 text-center">
                          <Badge variant={office.isActive ? "success" : "secondary"}>
                            {office.isActive ? (
                              <>
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3 w-3 mr-1" />
                                Inactive
                              </>
                            )}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditOffice(office)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {office.isActive ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(office)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleActivateOffice(office)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Form Sheet */}
      <OfficeFormSheet
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        office={selectedOffice}
        onSave={handleSaveOffice}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Office</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{officeToDelete?.name}</strong>? 
              This will not delete the office permanently, but users won't be able to select 
              it anymore. You can reactivate it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
